import React, { useMemo, useState } from "react";
import moment from "moment";
import { FiChevronDown } from "react-icons/fi";
import { CustomerCompleteLedgerColumns } from "../../assets/Columns/CustomerCompleteLedgerColumns";
import { filterTableRowsBySearch } from "../../utils/tableSearch";
import { sortLedgerRowsByDateDesc } from "../../utils/sortLedgerRows";
import { formatDateTime } from "../../utils/dateTime";

const formatRs = (n) =>
  `Rs ${Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

const formatDate = (row) => {
  const raw = row?.date;
  if (typeof raw === "number" && raw > 0) {
    return formatDateTime(raw);
  }
  if (row?.n_date && row.n_date !== "-") return row.n_date;
  return "—";
};

const isTotalRow = (row) => row?.desc === "Total";

const buildCardGroups = (rows) => {
  const groups = [];
  for (const row of rows) {
    if (isTotalRow(row)) {
      groups.push({ kind: "total", row });
      continue;
    }
    if (Number(row.dr) > 0 && row.name && row.name !== "-") {
      const key = `${row.invoice}-${row.date}`;
      const last = groups[groups.length - 1];
      if (last?.kind === "returnGroup" && last.key === key) {
        last.items.push(row);
        last.totalDr += Number(row.dr || 0);
        last.balance = row.balance;
      } else {
        groups.push({
          kind: "returnGroup",
          key,
          invoice: row.invoice,
          date: row.date,
          desc: row.desc,
          items: [row],
          totalDr: Number(row.dr || 0),
          balance: row.balance,
        });
      }
    } else if (Number(row.cr) > 0 && row.name && row.name !== "-") {
      const key = `${row.invoice}-${row.date}`;
      const last = groups[groups.length - 1];
      if (last?.kind === "saleGroup" && last.key === key) {
        last.items.push(row);
        last.totalCr += Number(row.cr || 0);
        last.balance = row.balance;
      } else {
        groups.push({
          kind: "saleGroup",
          key,
          invoice: row.invoice,
          date: row.date,
          desc: row.desc,
          items: [row],
          totalCr: Number(row.cr || 0),
          balance: row.balance,
        });
      }
    } else {
      groups.push({ kind: "single", row });
    }
  }
  return groups;
};

const ItemsToggle = ({ items, children }) => {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-main hover:underline"
      >
        <FiChevronDown
          size={11}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
        {open ? "Hide items" : `Show items (${items.length})`}
      </button>
      {open && (
        <div className="mt-1.5 rounded-md border border-line overflow-hidden max-h-40 overflow-y-auto">
          {children}
        </div>
      )}
    </div>
  );
};

const BalanceFooter = ({ balance }) => {
  if (balance == null || balance === "-") return null;
  return (
    <div className="mt-2 pt-2 border-t border-line flex justify-between items-center text-[10px]">
      <span className="text-muted">Balance</span>
      <span className="font-bold text-main tabular-nums">{formatRs(balance)}</span>
    </div>
  );
};

const ReturnGroupCard = ({ group }) => (
  <div className="rounded-lg border border-line bg-card px-3 py-2.5 shadow-soft">
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-orange-100 text-orange-800 border-orange-200">
        Return (Out)
      </span>
      {group.invoice && group.invoice !== "-" && (
        <span className="text-[10px] font-semibold text-muted">
          Inv #{group.invoice}
        </span>
      )}
    </div>

    <div className="flex items-baseline justify-between gap-2 mt-1.5">
      <p className="text-xs font-semibold text-ink">{formatDate(group)}</p>
      <p className="text-sm font-bold text-orange-700 tabular-nums">- {formatRs(group.totalDr)}</p>
    </div>

    {group.desc && group.desc !== "-" && (
      <p className="text-[11px] text-muted mt-0.5 line-clamp-1">{group.desc}</p>
    )}

    <ItemsToggle items={group.items}>
      {group.items.map((item, i) => (
        <div
          key={`${item.name}-${i}`}
          className={`px-2.5 py-1.5 text-[10px] ${
            i > 0 ? "border-t border-line" : ""
          } bg-subtle/50`}
        >
          <p className="font-semibold text-ink truncate">{item.name}</p>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-muted">
            {Number(item.qty) > 0 && <span>Qty: {item.qty}</span>}
            {item.price != null && Number(item.price) > 0 && (
              <span>U Price: {formatRs(item.price)}</span>
            )}
            <span className="font-semibold text-orange-600">{formatRs(item.dr)}</span>
          </div>
        </div>
      ))}
    </ItemsToggle>

    <BalanceFooter balance={group.balance} />
  </div>
);

const SaleGroupCard = ({ group }) => (
  <div className="rounded-lg border border-line bg-card px-3 py-2.5 shadow-soft">
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-successSoft text-success border-success/20">
        Sale (In)
      </span>
      {group.invoice && group.invoice !== "-" && (
        <span className="text-[10px] font-semibold text-muted">
          Inv #{group.invoice}
        </span>
      )}
    </div>

    <div className="flex items-baseline justify-between gap-2 mt-1.5">
      <p className="text-xs font-semibold text-ink">{formatDate(group)}</p>
      <p className="text-sm font-bold text-success tabular-nums">+ {formatRs(group.totalCr)}</p>
    </div>

    {group.desc && group.desc !== "-" && (
      <p className="text-[11px] text-muted mt-0.5 line-clamp-1">{group.desc}</p>
    )}

    <ItemsToggle items={group.items}>
      {group.items.map((item, i) => (
        <div
          key={`${item.name}-${i}`}
          className={`px-2.5 py-1.5 text-[10px] ${
            i > 0 ? "border-t border-line" : ""
          } bg-subtle/50`}
        >
          <p className="font-semibold text-ink truncate">{item.name}</p>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-muted">
            {Number(item.qty) > 0 && <span>Qty: {item.qty}</span>}
            {item.price != null && Number(item.price) > 0 && (
              <span>U Price: {formatRs(item.price)}</span>
            )}
            <span className="font-semibold text-success">{formatRs(item.cr)}</span>
          </div>
        </div>
      ))}
    </ItemsToggle>

    <BalanceFooter balance={group.balance} />
  </div>
);

const ReceivableTxnCard = ({ row }) => {
  const isTotal = isTotalRow(row);
  const isPayment = Number(row?.dr) > 0;
  const isReturn = isPayment && row?.name && row.name !== "-";

  return (
    <div
      className={`rounded-lg border bg-card px-3 py-2.5 shadow-soft ${
        isTotal ? "border-main bg-brandSoft" : "border-line"
      }`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span
          className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
            isReturn
              ? "bg-orange-100 text-orange-800 border-orange-200"
              : isPayment
              ? "bg-red-100 text-red-800 border-red-200"
              : isTotal
              ? "bg-main/10 text-main border-main/30"
              : "bg-subtle text-inkSecondary border-line"
          }`}
        >
          {isReturn ? "Return (Out)" : isPayment ? "Payment (Out)" : isTotal ? "Total" : "Entry"}
        </span>
        {!isTotal && row.invoice && row.invoice !== "-" && (
          <span className="text-[10px] font-semibold text-muted">
            Inv #{row.invoice}
          </span>
        )}
      </div>

      <div className="flex items-baseline justify-between gap-2 mt-1.5">
        <p className="text-xs font-semibold text-ink">{formatDate(row)}</p>
        {!isTotal && isPayment && (
          <p
            className={`text-sm font-bold tabular-nums ${
              isReturn ? "text-orange-700" : "text-red-700"
            }`}
          >
            - {formatRs(row.dr)}
          </p>
        )}
      </div>

      {row.desc && row.desc !== "-" && (
        <p className="text-[11px] text-muted mt-0.5 line-clamp-2">{row.desc}</p>
      )}

      {isReturn && row.name && (
        <ItemsToggle items={[row]}>
          <div className="px-2.5 py-1.5 text-[10px] bg-subtle/50">
            <p className="font-semibold text-ink">{row.name}</p>
            <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-muted">
              {Number(row.qty) > 0 && <span>Qty: {row.qty}</span>}
              {row.price != null && Number(row.price) > 0 && (
                <span>U Price: {formatRs(row.price)}</span>
              )}
            </div>
          </div>
        </ItemsToggle>
      )}

      {!isTotal && row.balance != null && row.balance !== "-" && (
        <BalanceFooter balance={row.balance} />
      )}

      {isTotal && (
        <div className="mt-2 pt-2 border-t border-line grid grid-cols-2 gap-2 text-[11px]">
          <div>
            <p className="text-muted">Total In (CR)</p>
            <p className="font-bold text-success tabular-nums">{formatRs(row.cr)}</p>
          </div>
          <div>
            <p className="text-muted">Total Out (DR)</p>
            <p className="font-bold text-red-700 tabular-nums">{formatRs(row.dr)}</p>
          </div>
          {row.balance != null && row.balance !== "-" && (
            <div className="col-span-2 pt-1.5 border-t border-line">
              <p className="text-muted">Net balance</p>
              <p className="text-sm font-bold text-main tabular-nums">{formatRs(row.balance)}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ReceivableTxnCardList = ({ rows, searchText, columns }) => {
  const filtered = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return sortLedgerRowsByDateDesc(
      filterTableRowsBySearch(
        list,
        searchText,
        columns || CustomerCompleteLedgerColumns
      )
    );
  }, [rows, searchText, columns]);

  const groups = useMemo(() => buildCardGroups(filtered), [filtered]);

  if (groups.length === 0) {
    return (
      <div className="text-center text-muted py-8 text-sm">
        No transactions found.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {groups.map((group, i) => {
        if (group.kind === "returnGroup") {
          return <ReturnGroupCard key={group.key} group={group} />;
        }
        if (group.kind === "saleGroup") {
          return <SaleGroupCard key={group.key} group={group} />;
        }
        if (group.kind === "total") {
          return <ReceivableTxnCard key={`total-${i}`} row={group.row} />;
        }
        return (
          <ReceivableTxnCard
            key={`${group.row.invoice}-${group.row.date}-${i}`}
            row={group.row}
          />
        );
      })}
    </div>
  );
};

export default ReceivableTxnCardList;
